/**
 * The one list of paths that render WITHOUT product chrome.
 *
 * `routes/__root.tsx` mounts `<Shell>` — mode rail, top nav, left/context rails,
 * dock — around every route. A design prototype whose purpose is to propose a
 * different information architecture cannot render inside that: the existing
 * rails would frame, and visually argue against, the thing being proposed.
 *
 * Rather than a `bareCanvas: true` flag each route could set (which spreads the
 * answer to "which URLs have no chrome?" across the whole route tree), the set
 * lives here as an explicit prefix list one grep away from the root route that
 * honours it.
 *
 * ── Relationship to `isTakeoverPath` (they are NOT the same thing) ──
 *
 * `shell/redirects.ts` already exports `isTakeoverPath` for `/login`, `/welcome`,
 * `/onboarding/workspace` and friends, and `Shell`'s own `ShellBody` honours it by
 * rendering children bare. That is a PRODUCT concept — "this screen takes over
 * because the user is not inside the app yet" — and it does its bypass INSIDE
 * `<Shell>`, so Shell still mounts and its effects still run. Two reasons this
 * prototype does not reuse it:
 *
 *   1. Adding `/styleguide/` to that list would file a design prototype under
 *      "onboarding takeover", conflating two unrelated reasons for no chrome.
 *   2. Shell's mounted effects include theme reconciliation, which re-asserts the
 *      user's real (dark, by default) theme on `<html>`. A light-mode prototype
 *      that renders inside Shell is therefore fighting the thing hosting it.
 *
 * So this bypass happens one level UP, in the root route, before `<Shell>` exists.
 *
 * The prefix is `/styleguide/` (prototype archive) plus exact `/` (production
 * verification shell, WP11b). A `/` prefix is NOT used: that would strip chrome
 * from every old-product compatibility route (`/work`, `/customers`, …). HG7
 * keeps those as direct-route compatibility; WP11b cut `/` only. The `/styleguide/`
 * namespace remains the repo's home for served design/architecture reads
 * (`excellent/public/styleguide/*.html`, and the `/styleguide` → verification-loop
 * redirect in `server/http.ts`).
 */

import { NAV_ITEMS, pathOf } from "../verification/nav";
import { isVerificationRollbackEngaged } from "../verification/rollback-switch";

const DEVELOPMENT_ONLY_PRODUCT_PATHS = new Set([
  "/buyable",
  "/demo-lab",
  "/design",
  "/dev",
  "/guide",
  "/skeletons",
]);

/** Registered for local design/QA, never exposed by a production bundle. */
export function isDevelopmentOnlyProductPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return (
    DEVELOPMENT_ONLY_PRODUCT_PATHS.has(normalized) ||
    normalized === "/styleguide" ||
    normalized.startsWith("/styleguide/")
  );
}

/**
 * Path prefixes whose routes render bare. Trailing slash keeps `/styleguidex`
 * out. `/settings/` is #46666. #46667 also keeps account-menu destinations
 * (help, database, directory, operations, agents/goals) in the verification
 * app. Prefix `/` is still NOT used (HG7 /work /customers /classic).
 */
const BARE_CANVAS_PREFIXES = [
  "/styleguide/",
  "/settings/",
  "/help/",
  "/database/",
  "/directory/",
  "/operations/",
  "/agents/goals/",
] as const;

/**
 * Exact paths that render bare (no trailing-slash form needed).
 *
 * `/` is the production verification shell (WP11b / #46645, HG1).
 * `/settings` is the WP9a landing. #46667 adds account-menu destinations
 * that are not already prefixes: help, notifications, favorites, database,
 * directory, operations, agents/goals. Prefix `/` is deliberately NOT used
 * (HG7 /work /customers /classic). Rail pages are also listed here as exact
 * paths (not a `/` prefix) so ModeRail cannot wrap them.
 */
const BARE_CANVAS_EXACT = [
  "/",
  "/styleguide",
  "/settings",
  "/help",
  "/notifications",
  "/favorites",
  "/database",
  "/directory",
  "/operations",
  "/agents/goals",
  "/queue",
  "/activity",
  "/findings",
  "/runs",
  "/workflows",
  "/controls",
  "/policies",
  "/evidence",
  "/connectors",
  "/receipts",
  "/packs",
  "/audit-room",
  "/workers",
  "/scorecard",
  "/runtime",
  "/sdk",
  "/trust",
  "/posture",
  "/guide",
  "/buyable",
  "/design",
] as const;

/** Settings landing or any settings leaf. */
export function isVerificationSettingsPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return normalized === "/settings" || normalized.startsWith("/settings/");
}

/** Settings leaf (not the WP9a landing, which already wraps VerificationShell). */
export function isVerificationSettingsLeaf(pathname: string): boolean {
  return pathname.startsWith("/settings/");
}

function matchesExactOrPrefix(pathname: string, exact: string, prefix: string): boolean {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return normalized === exact || pathname.startsWith(prefix);
}

/**
 * Retained product surfaces reachable from the verification account menu or
 * search commands. HG7 old-product routes (`/work`, `/customers`, `/classic`)
 * are not in this set.
 */
export function isVerificationRetainedPath(pathname: string): boolean {
  if (isVerificationSettingsPath(pathname)) return true;
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (
    normalized === "/help" ||
    normalized === "/notifications" ||
    normalized === "/favorites" ||
    normalized === "/database" ||
    normalized === "/directory" ||
    normalized === "/operations" ||
    normalized === "/agents/goals"
  ) {
    return true;
  }
  return (
    matchesExactOrPrefix(pathname, "/help", "/help/") ||
    matchesExactOrPrefix(pathname, "/database", "/database/") ||
    matchesExactOrPrefix(pathname, "/directory", "/directory/") ||
    matchesExactOrPrefix(pathname, "/operations", "/operations/") ||
    matchesExactOrPrefix(pathname, "/agents/goals", "/agents/goals/")
  );
}

/**
 * Bare-canvas retained pages that do not already mount VerificationShell.
 * Exact `/`, `/settings`, `/not-found`, and IA routes wrap the shell themselves.
 */
export function needsVerificationShellWrap(pathname: string): boolean {
  if (pathname === "/" || pathname === "/settings" || pathname === "/not-found") return false;
  if (isVerificationIaPath(pathname)) return false;
  return isVerificationRetainedPath(pathname);
}

/** Verification IA path (rail item or its `$id` child), including `/not-found`. */
export function isVerificationIaPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/not-found") return true;
  for (const item of NAV_ITEMS) {
    const p = pathOf(item);
    if (p === "/") continue;
    if (normalized === p || normalized.startsWith(`${p}/`)) return true;
  }
  return false;
}

/** True when `pathname` should render with no product chrome. */
export function isBareCanvasPath(pathname: string): boolean {
  // WP11c: rolled-back `/` restores the old shell WITH ModeRail/TopNav.
  if (pathname === "/" && isVerificationRollbackEngaged()) return false;
  if (isVerificationIaPath(pathname)) return true;
  if (isVerificationRetainedPath(pathname)) return true;
  if (BARE_CANVAS_EXACT.includes(pathname as (typeof BARE_CANVAS_EXACT)[number])) return true;
  return BARE_CANVAS_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}
